// use-bulk-drip-actions.ts — bulk pause/stop for the campaigns list (#590).
//
// The single-item delete (`performDelete` in use-drip-actions.ts) fires the
// backend abort in a try/catch and proceeds regardless. That is fine for one
// row the user is watching, but a BULK stop over N drips must not silently
// swallow the ones that failed to stop — the user needs "N stopped, M failed"
// so a still-sending campaign is not mistaken for a stopped one. So the bulk
// collector AWAITS each op and reports the partial-failure split.
//
// The collector itself is a pure function (`makeBulkDripActions`) parameterised
// by the abort/pause ops, so it is unit-testable with plain mocks — this repo
// tests pure functions, not wired hooks. `useBulkDripActions` is the thin hook
// that binds the real RTK mutations (unwrapped so a rejected request throws and
// is captured as a failure, not a resolved error envelope).

import React from 'react';
import { useAbortDripMutation, usePauseDripMutation } from '@common/reducers/backend-api/backend-api';

/** The outcome of a bulk op: which ids succeeded and which failed (with why). */
export interface BulkDripResult {
  ok: string[];
  failed: { id: string; error: unknown }[];
}

/** A single-id operation that resolves on success and REJECTS on failure. */
export type BulkDripOp = (id: string) => Promise<unknown>;

export interface BulkDripOps {
  /** Stop a drip (backend abort — flips its pending step campaign off `pending`). */
  abort: BulkDripOp;
  /** Pause a drip (backend pause — same stop-the-send write, reversible). */
  pause: BulkDripOp;
}

// Awaits each op in order and collects the ok/failed split. Sequential rather
// than Promise.all so a slow/failing op can't reorder the report and so we
// never fire a burst of writes the backend has to rate-limit.
async function runMany(ids: string[], op: BulkDripOp): Promise<BulkDripResult> {
  const ok: string[] = [];
  const failed: { id: string; error: unknown }[] = [];
  for (const id of ids) {
    try {
      await op(id);
      ok.push(id);
    } catch (error) {
      failed.push({ id, error });
    }
  }
  return { ok, failed };
}

/** Pure factory — bind the ops (real mutations, or mocks in a test). */
export function makeBulkDripActions(ops: BulkDripOps) {
  return {
    stopMany: (ids: string[]): Promise<BulkDripResult> => runMany(ids, ops.abort),
    pauseMany: (ids: string[]): Promise<BulkDripResult> => runMany(ids, ops.pause),
  };
}

export type BulkDripActions = ReturnType<typeof makeBulkDripActions>;

/** Hook: bind the real RTK drip mutations to the pure collector. */
export function useBulkDripActions(): BulkDripActions {
  const [abortDrip] = useAbortDripMutation();
  const [pauseDrip] = usePauseDripMutation();
  return React.useMemo(
    () => makeBulkDripActions({
      abort: (id: string) => abortDrip(id).unwrap(),
      pause: (id: string) => pauseDrip(id).unwrap(),
    }),
    [abortDrip, pauseDrip],
  );
}
